// Lv2: 段階予告（DB対応版）
import { useEffect, useState } from 'react';
import { Lv2Task } from '../types';
import { loadSettings } from '../lib/storage';
import { speak } from '../lib/speech';
import { Scheduler } from '../lib/scheduler';
import { generateLv2Message, Stage } from '../lib/messages';
import { appendReactionDb } from '../lib/db';
import DowPicker from '../components/DowPicker';
import StatusBanner from '../components/StatusBanner';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../contexts/AuthContext';

export default function Lv2Page() {
  const { family } = useAuth();
  const { tasks, add, update, remove } = useTasks('lv2');
  const [eventTime, setEventTime] = useState('16:00');
  const [title, setTitle] = useState('習い事');
  const [dow, setDow] = useState<number[]>([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!family) return;
    const scheduler = new Scheduler(
      () => (tasks as Lv2Task[]),
      async ({ task, stage }) => {
        if (task.level !== 'lv2' || !stage) return;
        const settings = loadSettings();
        const lv2 = task as Lv2Task;
        const msg = generateLv2Message(lv2, stage as Stage);
        try {
          const emphasis = stage === 'pre5' || stage === 'onTime' ? 'strong' : 'normal';
          await speak(msg, settings, { emphasis });
          await appendReactionDb(
            {
              taskId: task.id,
              taskTitle: lv2.title,
              level: 'lv2',
              firedAt: Date.now(),
              announcement: msg,
            },
            family.id
          );
        } catch (err) {
          console.error('[Lv2] 読み上げ失敗', err);
        }
      }
    );
    scheduler.start();
    setRunning(true);
    return () => {
      scheduler.stop();
      setRunning(false);
    };
  }, [family, tasks]);

  const handleAdd = async () => {
    if (!eventTime || !title.trim()) return;
    await add({
      level: 'lv2',
      eventTime,
      title: title.trim(),
      daysOfWeek: dow,
      enabled: true,
    } as Omit<Lv2Task, 'id' | 'createdAt'>);
    setTitle('');
    setDow([]);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('このタスクを削除しますか？')) return;
    await remove(id);
  };

  const handleToggle = async (t: Lv2Task) => {
    await update(t.id, { enabled: !t.enabled });
  };

  const handlePreviewAll = async (t: Lv2Task) => {
    const settings = loadSettings();
    const stages: Stage[] = ['pre30', 'pre15', 'pre5', 'onTime'];
    for (const s of stages) {
      try {
        const emphasis = s === 'pre5' || s === 'onTime' ? 'strong' : 'normal';
        await speak(generateLv2Message(t, s), settings, { emphasis });
        await new Promise((r) => setTimeout(r, 500));
      } catch (err) {
        console.error(err);
        break;
      }
    }
  };

  const lv2Tasks = tasks as Lv2Task[];

  return (
    <>
      <StatusBanner running={running} taskCount={lv2Tasks.filter((t) => t.enabled).length} />

      <div className="alert info">
        ℹ️ Lv2は <strong>「本イベント時刻」</strong> を登録します。
        30分前・15分前・5分前・0分の合計4回、自動でアナウンスされます。
      </div>

      <div className="card">
        <h2>Lv2 タスクを追加</h2>
        <p className="description">例：習い事が16時開始なら、本イベント時刻に「16:00」を設定。
        15:30に「あと30分で習い事だよ」が自動で流れます。</p>
        <div className="form-row-inline">
          <div className="form-row" style={{ minWidth: 140 }}>
            <label>本イベント時刻</label>
            <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} />
          </div>
          <div className="form-row" style={{ flex: 1, minWidth: 200 }}>
            <label>イベント名</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 習い事 / お風呂 / 宿題開始"
            />
          </div>
        </div>
        <div className="form-row">
          <label>曜日</label>
          <DowPicker value={dow} onChange={setDow} />
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>
          追加する
        </button>
      </div>

      <div className="card">
        <h2>登録済みタスク（{lv2Tasks.length}件）</h2>
        {lv2Tasks.length === 0 ? (
          <p className="muted">まだ何も登録されていません。</p>
        ) : (
          <div className="task-list">
            {lv2Tasks.map((t) => (
              <div key={t.id} className="task-item" style={{ opacity: t.enabled ? 1 : 0.5 }}>
                <div className="info">
                  <div className="time">{t.eventTime}</div>
                  <div className="msg">{t.title}</div>
                  <div className="meta">
                    {t.daysOfWeek.length === 0
                      ? '毎日'
                      : t.daysOfWeek.map((d) => '日月火水木金土'[d]).join('・')}
                    {' / 4段階予告'}
                  </div>
                </div>
                <div className="actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => handlePreviewAll(t)}>
                    試聴
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => handleToggle(t)}>
                    {t.enabled ? '停止' : '再開'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
